import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2, Lock } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const LOGO =
  "https://customer-assets.emergentagent.com/job_ed2227e8-f42f-486d-80b3-32e833e48098/artifacts/o750zne6_72B3D45F-1688-469F-935E-BB6BB3789CC9.png";

export default function AdminLogin() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user && user.role) navigate("/admin", { replace: true });
  }, [user, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const res = await login(email, password);
    setSubmitting(false);
    if (res.ok) navigate("/admin", { replace: true });
    else setError(res.error || "Login failed");
  };

  return (
    <main
      data-testid="admin-login-page"
      className="min-h-screen flex items-center justify-center bg-[#050B14] px-6 py-12"
    >
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-3 mb-12" data-testid="login-back-home">
          <img src={LOGO} alt="Capital Shield" className="h-11 w-11 object-contain" />
          <div>
            <div className="font-display text-lg font-bold tracking-wide">CAPITAL SHIELD</div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-[#9BD0E8]">
              Admin Console
            </div>
          </div>
        </Link>

        <div className="bg-[#0B1B3D] border border-white/10 p-8 md:p-10">
          <div className="flex items-center gap-3 mb-2">
            <Lock size={18} className="text-[#C9A24B]" />
            <div className="text-xs uppercase tracking-[0.25em] text-[#C9A24B] font-semibold">
              Secure Area
            </div>
          </div>
          <h1 className="font-display text-3xl font-bold mb-8" data-testid="login-title">
            Sign in
          </h1>

          <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs uppercase tracking-[0.18em] text-white/60 font-semibold">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email"
                placeholder="admin@capitalshield.ca"
                className="bg-[#050B14] border-white/15 text-white placeholder:text-white/30 rounded-none h-12 focus-visible:ring-[#C9A24B] focus-visible:ring-1"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs uppercase tracking-[0.18em] text-white/60 font-semibold">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password"
                className="bg-[#050B14] border-white/15 text-white placeholder:text-white/30 rounded-none h-12 focus-visible:ring-[#C9A24B] focus-visible:ring-1"
                required
              />
            </div>

            {error && (
              <div data-testid="login-error" className="text-sm text-red-400 border border-red-400/30 bg-red-400/5 px-4 py-3">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              data-testid="login-submit"
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 bg-[#C9A24B] hover:bg-[#E0B962] disabled:opacity-60 text-[#050B14] font-semibold text-sm tracking-wide transition-colors"
            >
              {submitting ? (
                <>
                  <Loader2 size={16} className="animate-spin" /> Signing in…
                </>
              ) : (
                "Sign in"
              )}
            </button>
          </form>
        </div>

        <Link
          to="/"
          className="block mt-8 text-center text-xs uppercase tracking-[0.25em] text-white/40 hover:text-[#C9A24B] transition-colors"
          data-testid="login-back-link"
        >
          ← Back to site
        </Link>
      </div>
    </main>
  );
}
